import { useEffect, useRef, useState } from 'react';

/**
 * useDerivedState — mirror a fetched value into local editable state.
 *
 * Common pattern for "load remote data → user edits a local draft":
 *
 *   const { data } = useQuery(...);
 *   const [tpl, setTpl] = useDerivedState(data);
 *   // user can mutate tpl; calling setTpl(...) updates local only.
 *   // when `data` changes (refetch) the local state re-mirrors it.
 *
 * For a "list with selection mirroring" pattern (used by McqBank /
 * VideoBotBank), pass a custom comparator/transformer:
 *
 *   const [selected, setSelected] = useDerivedSelection(qs, selected, (q) => q.id);
 *
 * Both helpers are intentionally tiny so the state-sync stays encapsulated
 * inside the hook (rather than at the call site).
 */
export function useDerivedState(remote) {
  const [local, setLocal] = useState(remote ?? null);
  const lastRemoteRef = useRef(remote);
  useEffect(() => {
    if (remote !== lastRemoteRef.current) {
      lastRemoteRef.current = remote;
      setLocal(remote ?? null);
    }
  }, [remote]);
  return [local, setLocal];
}

/**
 * useDerivedSelection — keep `selected` in sync with a freshly fetched
 * `list`, preserving the user's choice when possible.
 *
 *  - First load: selects the first item.
 *  - Subsequent fetches: if previously-selected item still exists, re-binds
 *    to the refreshed object (so changes propagate); else falls back to the
 *    first item or null when empty.
 *
 * `getKey` defaults to `(item) => item.id`.
 */
export function useDerivedSelection(list, getKey = (item) => item?.id) {
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    if (!list) return;
    if (!selected && list.length) {
      setSelected(list[0]);
      return;
    }
    if (selected) {
      const key = getKey(selected);
      const found = list.find((item) => getKey(item) === key);
      if (found && found !== selected) setSelected(found);
      else if (!found) setSelected(list[0] ?? null);
    }
    // We intentionally only depend on `list`. Including `selected` would make
    // every user click re-run this effect and could overwrite user edits.
  }, [list]);
  return [selected, setSelected];
}
